import React, { useState } from 'react'
import { Badge, ButtonBase, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useLastSeenRelease } from '../../hooks/useLastSeenRelease'
import ReleaseNotesDialog from '../release/ReleaseNotesDialog'

interface VersionDisplayProps {
  className?: string
  variant?: 'light' | 'dark'
  compact?: boolean
}

const VersionDisplay: React.FC<VersionDisplayProps> = ({ className, variant = 'light', compact = false }) => {
  const { currentVersion, unseen, markSeen } = useLastSeenRelease()
  const [dialogOpen, setDialogOpen] = useState(false)

  // Clear the unseen badge only once the user closes the notes (consistent
  // with the auto-popup) - opening alone shouldn't dismiss the notice.
  const handleClose = () => {
    setDialogOpen(false)
    void markSeen()
  }

  if (!currentVersion) {
    return null
  }

  const baseColor =
    variant === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.65)'
  const hoverColor =
    variant === 'dark' ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 0, 0, 0.9)'
  const hoverBg =
    variant === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'

  return (
    <>
      <Tooltip
        title={unseen ? `What's new in v${currentVersion}` : "What's new"}
        arrow
      >
        <ButtonBase
          focusRipple
          onClick={() => setDialogOpen(true)}
          className={className}
          aria-label={
            unseen
              ? `Show release notes for new version ${currentVersion}`
              : `Show release notes for version ${currentVersion}`
          }
          sx={{
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            gap: compact ? 0.25 : 0.5,
            flexDirection: compact ? 'column-reverse' : 'row',
            color: baseColor,
            transition: 'background-color 120ms, color 120ms',
            '&:hover': {
              color: hoverColor,
              backgroundColor: hoverBg,
              '& .version-label': { textDecoration: 'underline' },
            },
            '&:focus-visible': {
              outline: `2px solid ${hoverColor}`,
              outlineOffset: 2,
            },
          }}
        >
          <Badge
            color="success"
            variant="dot"
            invisible={!unseen}
            overlap="rectangular"
            sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}
          >
            <Typography
              variant="caption"
              className="version-label"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'inherit',
                textDecoration: 'underline dotted',
                textUnderlineOffset: '3px',
                textDecorationColor: 'currentColor',
              }}
            >
              v{currentVersion}
            </Typography>
          </Badge>
          <InfoOutlinedIcon
            sx={{
              fontSize: '0.95rem',
              color: 'inherit',
              opacity: 0.85,
            }}
          />
        </ButtonBase>
      </Tooltip>
      <ReleaseNotesDialog open={dialogOpen} onClose={handleClose} />
    </>
  )
}

export default VersionDisplay
